import React, { useMemo } from 'react';
import {
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormLabel,
    InputAdornment,
    Tooltip
} from '@mui/material';
import { Controller, Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import InfoIcon from '@mui/icons-material/Info';
import {
    CompensationTypeEnum,
    FormValues,
    FrequencyUnitTime,
    PatternStartDateEnum
} from 'schemas/DatePattern';

interface DatePatternFormFieldsProps {
    t: (key: string) => string;
    control: Control<FormValues>;
    register: UseFormRegister<FormValues>;
    errors: FieldErrors<FormValues>;
    setValue: UseFormSetValue<FormValues>;
    rows: any[];
    referenceDateOptions: any[];
    frequencyUnitTime: FrequencyUnitTime;
    patternStartDate: PatternStartDateEnum;
    compensationType: CompensationTypeEnum;
    nameValue?: string;
    disabled?: boolean;
}

const DatePatternFormFields: React.FC<DatePatternFormFieldsProps> = ({
    t,
    control,
    register,
    errors,
    setValue,
    rows,
    referenceDateOptions,
    frequencyUnitTime,
    patternStartDate,
    compensationType,
    nameValue,
    disabled = false
}) => {
    const frequencyOptions = useMemo(() => [
        { value: FrequencyUnitTime.DIAS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.days') },
        { value: FrequencyUnitTime.SEMANAS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.weeks') },
        { value: FrequencyUnitTime.MESES, label: t('accruals.date_patterns.dialog.inputs.options_frequency.months') },
        { value: FrequencyUnitTime.ANOS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.years') },
        { value: FrequencyUnitTime.PERIODO_PAGO, label: t('accruals.date_patterns.dialog.inputs.options_frequency.periods_of_pay') }
    ], [t]);

    const patternStartDateOptions = useMemo(() => [
        { value: PatternStartDateEnum.FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_of_the_reference_date') },
        { value: PatternStartDateEnum.CERCANO_FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_as_close_as_possible_to_the_reference_date') },
        { value: PatternStartDateEnum.ANTES_FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_on_or_before_the_reference_date') },
        { value: PatternStartDateEnum.DESPUES_FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_on_or_after_the_reference_date') }
    ], [t]);

    const compensationTypeOptions = useMemo(() => [
        { value: CompensationTypeEnum.ANTES, label: t('accruals.date_patterns.dialog.inputs.options_chronological_difference.before') },
        { value: CompensationTypeEnum.DESPUES, label: t('accruals.date_patterns.dialog.inputs.options_chronological_difference.after') }
    ], [t]);

    const daysOfWeekOptions = useMemo(() => [
        { value: 1, label: t('general.days_week.monday') },
        { value: 2, label: t('general.days_week.tuesday') },
        { value: 3, label: t('general.days_week.wednesday') },
        { value: 4, label: t('general.days_week.thursday') },
        { value: 5, label: t('general.days_week.friday') },
        { value: 6, label: t('general.days_week.saturday') },
        { value: 7, label: t('general.days_week.sunday') }
    ], [t]);

    const monthOfYear = useMemo(() => [
        { value: 1, label: t('general.months.january') },
        { value: 2, label: t('general.months.february') },
        { value: 3, label: t('general.months.march') },
        { value: 4, label: t('general.months.april') },
        { value: 5, label: t('general.months.may') },
        { value: 6, label: t('general.months.june') },
        { value: 7, label: t('general.months.july') },
        { value: 8, label: t('general.months.august') },
        { value: 9, label: t('general.months.september') },
        { value: 10, label: t('general.months.october') },
        { value: 11, label: t('general.months.november') },
        { value: 12, label: t('general.months.december') }
    ], [t]);

    return (
        <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <TextField
                        fullWidth
                        label={t('accruals.date_patterns.dialog.inputs.name')}
                        autoComplete="off"
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        slotProps={{
                            htmlInput: { maxLength: 100 },
                            input: {
                                endAdornment: <InputAdornment position="end">{`${nameValue?.length ?? 0}/100`}</InputAdornment>,
                            },
                        }}
                        required
                        disabled={disabled}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Controller
                        name="reference_date_id"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth error={!!errors.reference_date_id} required disabled={disabled}>
                                <InputLabel id="date-reference-select-label">{t('accruals.date_patterns.dialog.inputs.date_reference')}</InputLabel>
                                <Select
                                    {...field}
                                    labelId="date-reference-select-label"
                                    id="date-reference-select"
                                    fullWidth
                                    label={t('accruals.date_patterns.dialog.inputs.date_reference')}
                                    disabled={referenceDateOptions.length === 0 || disabled}
                                >
                                    <MenuItem value={0}>{'Fecha de contratación'}</MenuItem>
                                    {referenceDateOptions.map((option: any) => (
                                        <MenuItem key={option.accrual_date_id} value={option.accrual_date_id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.reference_date_id && (
                                    <FormLabel error sx={{ mt: 0.5, ml: 1.75, fontSize: '0.75rem' }}>
                                        {errors.reference_date_id.message}
                                    </FormLabel>
                                )}
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <Controller
                        name="frequency_unit_time"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth disabled={disabled}>
                                <InputLabel id="frequency-amount-select-label">{t('accruals.date_patterns.dialog.inputs.frequency')}</InputLabel>
                                <Select
                                    {...field}
                                    labelId="frequency-amount-select-label"
                                    id="frequency-amount-select"
                                    fullWidth
                                    label={t('accruals.date_patterns.dialog.inputs.frequency')}
                                >
                                    {frequencyOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                    <MenuItem disabled value="Definido por el usuario">
                                        {'Definido por el usuario'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <TextField
                        type="number"
                        id="frequency-amount-input"
                        slotProps={{
                            htmlInput: {
                                min: 1,
                                max: 100
                            },
                            inputLabel: {
                                shrink: true
                            }
                        }}
                        placeholder="1"
                        fullWidth
                        {...register('frequency_amount', { valueAsNumber: true })}
                        error={!!errors.frequency_amount}
                        helperText={errors.frequency_amount?.message}
                        label={t('accruals.date_patterns.dialog.inputs.frequency_amount')}
                        disabled={disabled}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Controller
                        name="pattern_start_date"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth disabled={disabled}>
                                <InputLabel id="pattern-start-date-select-label">{t('accruals.date_patterns.dialog.inputs.start_of_pattern')}</InputLabel>
                                <Select
                                    {...field}
                                    labelId="pattern-start-date-select-label"
                                    id="pattern-start-date-select"
                                    fullWidth
                                    label={t('accruals.date_patterns.dialog.inputs.start_of_pattern')}
                                >
                                    {patternStartDateOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>

                {frequencyUnitTime === FrequencyUnitTime.SEMANAS && patternStartDate !== PatternStartDateEnum.FECHA_REFERENCIA && (
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <Controller
                            name="day_week_to_start"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth disabled={disabled}>
                                    <InputLabel id="day-of-week-to-start-select-label">{t('accruals.date_patterns.dialog.inputs.day_of_the_week_to_start')}</InputLabel>
                                    <Select
                                        {...field}
                                        fullWidth
                                        label={t('accruals.date_patterns.dialog.inputs.day_of_the_week_to_start')}
                                    >
                                        {daysOfWeekOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                )}

                {frequencyUnitTime === FrequencyUnitTime.MESES && patternStartDate !== PatternStartDateEnum.FECHA_REFERENCIA && (
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <Controller
                            name="day_month_to_start"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth disabled={disabled}>
                                    <InputLabel id="day-of-month-to-start-select-label">{t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}</InputLabel>
                                    <Select
                                        {...field}
                                        fullWidth
                                        label={t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}
                                    >
                                        {[...Array(31)].map((_: any, index) => (
                                            <MenuItem key={index + 1} value={index + 1}>
                                                {index + 1}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                )}

                {frequencyUnitTime === FrequencyUnitTime.ANOS && patternStartDate !== PatternStartDateEnum.FECHA_REFERENCIA && (
                    <>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Controller
                                name="month_to_start"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth disabled={disabled}>
                                        <InputLabel id="month-to-start-select-label">{t('accruals.date_patterns.dialog.inputs.month_of_the_year_to_start')}</InputLabel>
                                        <Select
                                            {...field}
                                            fullWidth
                                            label={t('accruals.date_patterns.dialog.inputs.month_of_the_year_to_start')}
                                        >
                                            {monthOfYear.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Controller
                                name="day_month_to_start"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth disabled={disabled}>
                                        <InputLabel id="day-month-to-start-select-label">{t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}</InputLabel>
                                        <Select
                                            {...field}
                                            fullWidth
                                            label={t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}
                                        >
                                            {[...Array(31)].map((_: any, index) => (
                                                <MenuItem key={index + 1} value={index + 1}>
                                                    {index + 1}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </>
                )}

                {frequencyUnitTime === FrequencyUnitTime.PERIODO_PAGO && patternStartDate !== PatternStartDateEnum.FECHA_REFERENCIA && (
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <Controller
                            name="day_period_to_start"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth disabled={disabled}>
                                    <InputLabel id="day-of-period-to-start-select-label">{t('accruals.date_patterns.dialog.inputs.payment_period_start_date')}</InputLabel>
                                    <Select
                                        {...field}
                                        fullWidth
                                        label={t('accruals.date_patterns.dialog.inputs.payment_period_start_date')}
                                    >
                                        {[...Array(31)].map((_: any, index) => (
                                            <MenuItem key={index + 1} value={index + 1}>
                                                {index + 1}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                )}

                <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <Controller
                        name="compensation_type"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth disabled={disabled}>
                                <InputLabel id="compensation-type-select-label">{t('accruals.date_patterns.dialog.inputs.chronological_difference')}</InputLabel>
                                <Select
                                    {...field}
                                    labelId="compensation-type-select-label"
                                    id="compensation-type-select"
                                    fullWidth
                                    label={t('accruals.date_patterns.dialog.inputs.chronological_difference')}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        const value = e.target.value as CompensationTypeEnum;
                                        if (value === CompensationTypeEnum.NONE) {
                                            setValue('compensation_number', 0);
                                            setValue('compensation_pattern_date_id', 0);
                                        }
                                    }}
                                >
                                    <MenuItem disabled value="disabled">
                                        <em>{t('accruals.date_patterns.dialog.inputs.chronological_difference')}</em>
                                    </MenuItem>
                                    <MenuItem key={'none'} value={'none'}>
                                        {t('accruals.date_patterns.dialog.inputs.options_chronological_difference.none')}
                                    </MenuItem>
                                    {compensationTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <TextField
                        type="number"
                        id="compensation-number-after"
                        slotProps={{
                            htmlInput: {
                                min: 1,
                                max: 100
                            },
                            inputLabel: {
                                shrink: true
                            }
                        }}
                        fullWidth
                        placeholder="1"
                        {...register('compensation_number', { valueAsNumber: true })}
                        disabled={compensationType === CompensationTypeEnum.NONE || disabled}
                        label={t('accruals.date_patterns.dialog.inputs.chronological_difference_amount')}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Controller
                        name="compensation_pattern_date_id"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth disabled={disabled}>
                                <InputLabel id="compensation-pattern-date-select-label">{t('accruals.date_patterns.dialog.inputs.date_pattern')}</InputLabel>
                                <Select
                                    {...field}
                                    labelId="compensation-pattern-date-select-label"
                                    id="compensation-pattern-date-select"
                                    fullWidth
                                    label={t('accruals.date_patterns.dialog.inputs.date_pattern')}
                                    renderValue={(selected) => {
                                        if (selected === 0) {
                                            return <em>Seleccione un patrón de fecha</em>;
                                        }
                                        if (selected === -1) {
                                            return t('accruals.date_patterns.dialog.inputs.options_chronological_difference.none');
                                        }

                                        const selectedOption = rows.find((option: any) => option.id === selected);
                                        return selectedOption ? selectedOption.name : selected;
                                    }}
                                    disabled={compensationType === CompensationTypeEnum.NONE || disabled}
                                >
                                    <MenuItem value={0} disabled>
                                        <em>{t('accruals.date_patterns.dialog.inputs.date_pattern')}</em>
                                    </MenuItem>
                                    <MenuItem value={-1}>
                                        <em>{t('accruals.date_patterns.dialog.inputs.options_chronological_difference.none')}</em>
                                    </MenuItem>
                                    {rows.map((option: any) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <FormLabel component="legend" sx={{ mb: 2 }}>
                        {t('accruals.date_patterns.dialog.inputs.full_time_equivalent')}
                    </FormLabel>
                    <TextField
                        label={t('accruals.date_patterns.dialog.inputs.estimated_hours_worked_during_this_pattern')}
                        fullWidth
                        autoComplete="off"
                        placeholder="HH:MM ó HHH:MM"
                        {...register('full_time_equivalent')}
                        error={!!errors.full_time_equivalent}
                        helperText={errors.full_time_equivalent?.message}
                        slotProps={{
                            input: {
                                endAdornment: errors.full_time_equivalent && (
                                    <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                        <Tooltip title={errors.full_time_equivalent.message}>
                                            <InfoIcon color="error" />
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }
                        }}
                        disabled={disabled}
                    />
                </Grid>
        </Grid>
    );
};

export { DatePatternFormFields };
